import * as React from "react";
import { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import {
  SnapshotEventsContext,
  type SnapshotEventsContextValue,
} from "@/modules/snapshots/snapshotEvents/useSnapshotEventsContext";
import {
  useGetSnapshotEventsQuery,
  useMakeSnapshotEventCompletedMutation,
  useMakeSnapshotEventRecipientCompletedMutation,
  useSendSnapshotEventRecipientRemindMutation,
  useSendSnapshotEventRemindMutation,
} from "@/modules/snapshots/businessReports.api";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router";

interface Props {
  children: React.ReactNode;
}

export const SnapshotEventsProvider = ({ children }: Props) => {
  const [searchParams] = useSearchParams();
  const [businessReportOutcomeId = undefined] = searchParams.getAll("businessReportOutcomeId");
  const [documentName = undefined] = searchParams.getAll("documentName");

  const { data, isSuccess } = useGetSnapshotEventsQuery({
    businessReportOutcomeId,
    documentName,
  });

  const [selectedSnapshotEventGroupIds, setSelectedSnapshotEventGroupIds] = React.useState<string[] | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<GetSnapshotEventResponseItem | null>(null);
  const [selectedEventToEdit, setSelectedEventToEdit] = React.useState<GetSnapshotEventResponseItem | null>(null);

  const [sendSnapshotEventRemindMutation] = useSendSnapshotEventRemindMutation();
  const [sendSnapshotEventRecipientRemindMutation] = useSendSnapshotEventRecipientRemindMutation();
  const [makeSnapshotEventRecipientCompletedMutation] = useMakeSnapshotEventRecipientCompletedMutation();
  const [makeSnapshotEventCompletedMutation] = useMakeSnapshotEventCompletedMutation();

  const getCompletionRate = React.useCallback((event: GetSnapshotEventResponseItem) => {
    const total = event.required_only_one_done
      ? 1
      : event.recipients.filter(recipient => !recipient.is_readonly).length;
    const completed = event.recipients.filter(({ status }) => status === "done").length;
    if (total === 0) {
      return 100;
    }
    if (event.status === "done") {
      return 100;
    }
    return Math.round((completed * 100) / total);
  }, []);

  const sendReminderToRecipient = React.useCallback(
    async (recipientId: string) => {
      try {
        await sendSnapshotEventRecipientRemindMutation(recipientId);
        toast.success("Email was sent successfully");
      } catch (_e) {
        toast.error("Failed to send reminder email");
      }
    },
    [sendSnapshotEventRecipientRemindMutation],
  );

  const sendRemindersToAllRecipients = React.useCallback(
    async (eventId: string) => {
      await sendSnapshotEventRemindMutation(eventId);
      toast.success("Emails were sent successfully");
    },
    [sendSnapshotEventRemindMutation],
  );

  const makeSnapshotEventCompleted = React.useCallback(
    async (eventId: string) => {
      await makeSnapshotEventCompletedMutation(eventId);

      toast.success("Event has been marked as completed");
    },
    [makeSnapshotEventCompletedMutation],
  );

  const makeSnapshotEventRecipientCompleted = React.useCallback(
    async (recipientId: string) => {
      await makeSnapshotEventRecipientCompletedMutation(recipientId);
      toast.success("Event has been marked as completed");
    },
    [makeSnapshotEventRecipientCompletedMutation],
  );

  const selectedSnapshotEventGroups = React.useMemo(() => {
    if (!data || !isSuccess || !selectedSnapshotEventGroupIds) {
      return null;
    }

    return data.filter(({ id }) => selectedSnapshotEventGroupIds.includes(id));
  }, [data, isSuccess, selectedSnapshotEventGroupIds]);

  const value: SnapshotEventsContextValue = {
    selectedSnapshotEventGroupIds,
    setSelectedSnapshotEventGroupIds,
    selectedEvent,
    setSelectedEvent,
    selectedEventToEdit,
    setSelectedEventToEdit,
    getCompletionRate,
    sendReminderToRecipient,
    sendRemindersToAllRecipients,
    makeSnapshotEventCompleted,
    makeSnapshotEventRecipientCompleted,
    selectedSnapshotEventGroups,
  };

  return <SnapshotEventsContext.Provider value={value}>{children}</SnapshotEventsContext.Provider>;
};
